import { Icon } from '@drdropin-tech/theseus';
import { useBannerStore } from '@packages/shared';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import Image from 'next/image';
import { useTranslation } from 'next-i18next';
import { shallow } from 'zustand/shallow';

import FeedbackForm from './FeedbackForm';
import useBreakPoints from './useBreakPoints';

export default function BannerExport(): JSX.Element {
  const { isLg } = useBreakPoints('lg');
  if (isLg) return <DanielBanner />;
  return <Banner />;
}

function Banner() {
  const { t } = useTranslation('common');
  const { bannerState, closeBanner, formState, setFormState } = useBannerStore(
    (state) => ({
      bannerState: state.bannerState,
      closeBanner: state.closeBanner,
      formState: state.formState,
      setFormState: state.setFormState,
    }),
    shallow,
  );

  if (bannerState === 'hidden') return <></>;
  return (
    <>
      <motion.aside
        className="fixed bottom-0 z-10 flex w-full justify-center"
        id="feedback-banner"
        variants={bannerContainer}
        initial="closed"
        animate={bannerState === 'open' ? 'open' : 'closed'}
      >
        <div className="relative z-20 mx-5 flex w-full justify-between rounded-t-md bg-white transition-all md:w-auto">
          <div className="flex w-full items-center justify-center gap-2 p-4 text-center text-black shadow-lg">
            {formState === '' ? (
              <div className="flex w-full items-center justify-between gap-2">
                <span className="col-span-2 text-left text-xs">
                  {t('banner.header')}
                </span>
                <div className="flex gap-2">
                  <button
                    className="ring-primary rounded-full border bg-black px-6 py-3 text-xs font-medium text-white shadow-sm outline-none transition-all focus:ring-2 focus:ring-offset-2"
                    onClick={() => {
                      setFormState('unanswered');
                    }}
                    data-testid="feedback-banner-no"
                  >
                    {t('no')}
                  </button>
                  <button
                    className="ring-primary rounded-full border bg-white px-6 py-3 text-xs font-medium shadow-sm outline-none transition-all focus:ring-2 focus:ring-offset-2"
                    onClick={() => {
                      closeBanner();
                    }}
                    data-testid="feedback-banner-yes"
                  >
                    {t('yes')}
                  </button>
                </div>
              </div>
            ) : null}
            {formState === 'unanswered' ? <FeedbackForm /> : null}
            {formState === 'answered' ? <BannerComplete /> : null}
          </div>
        </div>
      </motion.aside>
    </>
  );
}

function BannerComplete() {
  const { t } = useTranslation();
  const { setFormState, closeBanner } = useBannerStore(
    (state) => ({
      setFormState: state.setFormState,
      closeBanner: state.closeBanner,
    }),
    shallow,
  );
  return (
    <div className="flex flex-col items-center justify-center gap-2">
      <span className="text-sm">{t('banner.thanks')}</span>
      <button
        className="ring-primary rounded-full border bg-black px-6 py-3 text-xs font-medium text-white shadow-sm outline-none transition-all focus:ring-2 focus:ring-offset-2"
        autoFocus
        onClick={() => {
          setFormState('answered');
          closeBanner();
        }}
      >
        {t('banner.form.form_close_button')}
      </button>
    </div>
  );
}

const container = {
  open: {
    opacity: 1,
    transition: {
      staggerChildren: 0.4,
    },
  },
  closed: { opacity: 1 },
};
const textbox = {
  open: { opacity: 1 },
  closed: { opacity: 0 },
};

function DanielBanner() {
  const { t } = useTranslation();
  const { bannerState, closeBanner, formState, setFormState } = useBannerStore(
    (state) => ({
      bannerState: state.bannerState,
      openBanner: state.openBanner,
      closeBanner: state.closeBanner,
      formState: state.formState,
      setFormState: state.setFormState,
    }),
    shallow,
  );

  if (bannerState === 'hidden') return <></>;

  return (
    <motion.div
      initial="closed"
      animate={bannerState === 'open' ? 'open' : 'closed'}
      variants={container}
      className={classNames('fixed bottom-0 z-50 flex flex-col')}
    >
      <div className="z-200 absolute bottom-0 left-0 ml-4 flex flex-row-reverse items-end">
        <motion.div
          className="z-5 h-44 w-44"
          variants={{
            open: { opacity: 1, y: 0 },
            closed: { opacity: 0, y: '125%' },
          }}
        >
          <Image
            src="/illustrations/daniel.png"
            alt=""
            aria-hidden="true"
            fill
          />
        </motion.div>
      </div>
      <motion.div
        variants={textbox}
        className={classNames(
          'prose-xs left-50 z-70 absolute bottom-10 w-80 rounded-lg bg-white p-6 text-sm text-black shadow-xl',
        )}
      >
        <div className="absolute -left-2 bottom-20 z-0 rotate-45 bg-white p-3" />
        <button
          type="button"
          className="z-200 border-primary/50 ring-primary absolute right-2 top-2 m-2 rounded-full border-none bg-white placeholder-black/50 outline-none transition-all focus:ring-2 focus:ring-offset-2"
          onClick={() => {
            closeBanner();
          }}
          aria-label={t('banner.close_aria_label')}
        >
          <Icon name="X" aria-hidden="true" className="text-black" />
        </button>
        {formState === '' ? (
          <div className="flex w-full flex-col items-center justify-center">
            <p className="z-70 col-span-2 mb-4 pr-4">{t('banner.header')} </p>
            <div className="flex gap-2">
              <button
                className="ring-primary inline-flex items-center self-end rounded-full border bg-black px-6 py-3 text-xs font-medium text-white shadow-sm outline-none transition-all focus:ring-2 focus:ring-offset-2"
                onClick={() => {
                  setFormState('unanswered');
                }}
                data-testid="feedback-banner-no"
              >
                {t('no')}
              </button>
              <button
                className="ring-primary inline-flex items-center self-end rounded-full border bg-white px-6 py-3 text-xs font-medium shadow-sm outline-none transition-all focus:ring-2 focus:ring-offset-2"
                onClick={() => {
                  closeBanner();
                }}
                data-testid="feedback-banner-yes"
              >
                {t('yes')}
              </button>
            </div>
          </div>
        ) : null}
        {formState === 'unanswered' ? <FeedbackForm /> : null}
        {formState === 'answered' ? <BannerComplete /> : null}
      </motion.div>
    </motion.div>
  );
}

const bannerContainer = {
  open: {
    opacity: 1,
    translateY: '0%',
    transition: {
      duration: 0.3,
      ease: 'easeInOut',
    },
  },
  closed: {
    opacity: 0,
    translateY: '100%',
  },
};
